/**
 * The **ingress** filter builder (ADR-040 §D-6; REQ-UI-160; Flow V Frame 6).
 *
 * The clause tree itself is shared verbatim with the egress twin — same document
 * model, same clause kinds, same recursive editor — so this composes
 * :func:`@/peers/peer-filter-builder.PeerFilterBuilder` rather than restating
 * 350 lines of it (CLAUDE.md §4.4). What it adds is everything the *direction*
 * changes:
 *
 *   - **the intake statement** above the tree, which inverts (see
 *     :func:`describeIntakeReading`);
 *   - **a *remove filter* affordance**, which the egress surface deliberately
 *     does not have.
 *
 * # Why removing a filter confirms but does not warn
 *
 * On egress an "allow everything" control is deliberately absent, so waving the
 * whole firehose through stays a code decision. On ingress allow-everything is
 * the **default** and the interesting act is the narrowing one — so the
 * affordance that needs to exist is the one that returns an interface to
 * accepting everything.
 *
 * It **confirms** because widening collection is an operator decision worth
 * recording. It does **not warn**, because the state it returns to is the
 * fail-safe one: what a filter excludes is not stored, not forwarded and not
 * recoverable, so removing one can only ever cause more to be kept. Dressing
 * that up as a hazard would train an operator to click through the warnings that
 * matter.
 *
 * Cross-references:
 *   - ADR-040 §D-6; REQ-PROVIDER-037 (the allow-all fail-safe).
 *   - REQ-UI-160 (the inversion, the standing notice, the remove affordance).
 *   - REQ-UI-059 (keyboard + labelled controls); REQ-UI-040 (both themes).
 */
import { useMemo, useState } from "react";
import { PeerFilterBuilder } from "@/peers/peer-filter-builder";
import type { ClauseNode } from "@/peers/peer-model";
import { describeIntakeReading, type IntakePosture } from "@/providers/provider-filter-tree";

/** Props for {@link ProviderFilterBuilder}. */
export interface ProviderFilterBuilderProps {
  /** The current root predicate, or ``null`` — which here means *receives everything*. */
  root: ClauseNode | null;
  onChange: (next: ClauseNode | null) => void;
  /** Greys out the SAPIENT-only selectors for a raw interface. */
  payloadDomain: "sapient" | "raw";
  readOnly?: boolean;
}

/**
 * Per-posture presentation.
 *
 * ``receives-everything`` is deliberately **neutral**, not warning-toned: it is
 * the fail-safe state, and painting the safe default amber would put it in the
 * same visual class as the starved one, which is the actual alarm. The
 * ``narrowing`` notice is informational-but-standing — it is a statement of
 * consequence, not a fault. Only ``starved`` is an alarm, and it is the one
 * posture where records are being silently discarded.
 */
const POSTURE_PRESENTATION: Record<IntakePosture, { className: string; role: "status" | "alert" }> =
  {
    "receives-everything": {
      className:
        "rounded border border-border bg-bg-elevated px-2 py-1.5 text-meta text-text-secondary",
      role: "status",
    },
    narrowing: {
      className:
        "rounded border border-border bg-bg-elevated px-2 py-1.5 text-meta text-text-primary",
      role: "status",
    },
    // ⚠ Border-only alarm, no `bg-destructive/10`. `text-destructive` over its own
    // 10% tint composites to ~4.15:1 in the light theme, under REQ-UI-046's 4.5:1
    // text bar; on the plain panel the same ink is ~5.06:1. The border carries the
    // alarm as a non-text channel and the words carry the meaning. (Measured on
    // the egress twin's §5.11 pass — every unit test was green, because jsdom
    // composites nothing.)
    starved: {
      className: "rounded border border-destructive px-2 py-1.5 text-meta text-destructive",
      role: "alert",
    },
  };

/** Render the ingress filter builder for one interface. */
export function ProviderFilterBuilder({
  root,
  onChange,
  payloadDomain,
  readOnly = false,
}: ProviderFilterBuilderProps): React.ReactElement {
  const reading = useMemo(() => describeIntakeReading(root), [root]);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const presentation = POSTURE_PRESENTATION[reading.posture];

  return (
    <div className="flex flex-col gap-2" data-testid="provider-filter">
      <PeerFilterBuilder
        root={root}
        onChange={onChange}
        payloadDomain={payloadDomain}
        readOnly={readOnly}
        testId="provider-filter-builder"
        notice={
          <p
            role={presentation.role}
            data-testid={`provider-filter-${reading.posture}`}
            className={presentation.className}
          >
            {reading.text}
          </p>
        }
      />

      {/* Only offered once there is something to remove. On an interface with no
          document the control would be a no-op that implies the opposite state
          exists to return from. */}
      {!readOnly && root !== null && (
        <div className="flex items-center gap-2">
          {confirmRemove ? (
            <div
              // `status`, not `alert`: this confirmation widens collection back
              // to the fail-safe default. It asks a question; it does not report
              // a hazard.
              role="status"
              data-testid="provider-filter-remove-confirm"
              className="flex flex-wrap items-center gap-2 rounded border border-border px-2 py-1 text-meta text-text-primary"
            >
              <span className="flex-1">
                Remove this filter? The interface will accept everything its provider sends, which
                is the default for a new interface.
              </span>
              <button
                type="button"
                onClick={() => {
                  setConfirmRemove(false);
                  onChange(null);
                }}
                data-testid="provider-filter-remove-go"
                className="rounded border border-border px-2 py-0.5 text-text-primary hover:bg-bg-elevated"
              >
                Remove filter
              </button>
              <button
                type="button"
                onClick={() => setConfirmRemove(false)}
                data-testid="provider-filter-remove-cancel"
                className="rounded border border-border px-2 py-0.5 text-text-primary hover:bg-bg-elevated"
              >
                Keep it
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmRemove(true)}
              data-testid="provider-filter-remove"
              className="rounded border border-border px-2 py-0.5 text-meta text-text-secondary hover:bg-bg-elevated"
            >
              Remove filter&hellip;
            </button>
          )}
        </div>
      )}
    </div>
  );
}
